import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, isRef as _isRef, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "ms-1"
}
const _hoisted_2 = {
  key: 1,
  class: "ms-1"
}
const _hoisted_3 = {
  key: 0,
  class: "loading-spinner"
}
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { key: 0 }
const _hoisted_8 = {
  key: 2,
  class: "loading-spinner"
}
const _hoisted_9 = { key: 3 }
const _hoisted_10 = { class: "mt-3" }
const _hoisted_11 = { key: 0 }

import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useQuery, useMutation, useLazyQuery } from '@vue/apollo-composable';
import {
  UserDocument,
  IamUserRolesDocument,
  UserQuery,
  UserRoleType,
  IamUserRoleAssignDocument,
  IamUserRoleRevokeDocument,
} from '@/gql/graphql';
import { deepParseDates, formatDateTime } from '@/utils/date-utils';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  BAlert,
  BBadge,
  BButton,
  BFormCheckbox,
  BSpinner,
} from 'bootstrap-vue-next';
import { openRouterLink } from '@/utils/router-utils';


export default /*@__PURE__*/_defineComponent({
  __name: 'AdminUserDetailView',
  setup(__props) {

const route = useRoute();

// Compute the user ID by converting the route param to a number
const userId = computed(() => {
  const id = route.params.id as string;
  const userId = Number(id);
  if (isNaN(userId)) {
    throw new Error('Invalid user ID');
  }
  return userId;
});
const userEmail = computed(() => userQueryResult.value?.user?.email ?? '');

// --- USER QUERY ---
const {
  result: userQueryResult,
  loading: userQueryInProgress,
  error: userQueryError,
  refetch: refetchUser,
} = useQuery(
  UserDocument,
  { id: userId.value },
  {
    enabled: computed(() => Boolean(userId.value)),
  }
);

const userData = computed<NonNullable<UserQuery['user']> | null>(() => {
  console.debug('fetched user:', userQueryResult.value?.user);
  if (userQueryResult.value?.user) {
    return deepParseDates(userQueryResult.value.user) as NonNullable<
      UserQuery['user']
    >;
  }
  return null;
});

// --- USER ROLES QUERY ---
const {
  load: fetchUserRoles,
  result: userRolesQueryResult,
  loading: userRolesQueryInProgress,
  error: userRolesQueryError,
} = useLazyQuery(IamUserRolesDocument);

const userRoles = computed(() => {
  if (userRolesQueryResult.value?.iamUserRoles) {
    return userRolesQueryResult.value.iamUserRoles.clients;
  }
  return null;
});

function requestRefetchUserRoles() {
  if (!userEmail.value) {
    console.error('User email is not available for refetching user roles');
    return;
  }
  // Failures show through userRolesQueryError, not as unhandled rejections.
  const request = fetchUserRoles(IamUserRolesDocument, {
    userEmail: userEmail.value,
  });
  if (request) request.catch(() => undefined);
}

// --- ASSIGN NEW User Role ---
const assignRoleMutationError = ref<string | null>(null);
const assignRoleMutationInProgress = ref(false);
const assignRoleMutationShowSuccessIndicator = ref(false);

const {
  mutate: assignRoleMutate,
  onDone: onAssignRoleDone,
  onError: onAssignRoleError,
} = useMutation(IamUserRoleAssignDocument, {
  refetchQueries: [
    {
      query: IamUserRolesDocument,
      variables: { userEmail: userEmail.value },
    },
  ],
  variables: {
    clientId: '',
    userEmail: userEmail.value,
    userRole: '',
  },
});

onAssignRoleDone((res) => {
  if (res.data && res.data.iamUserRoleAssign) {
    assignRoleMutationError.value = null;
    assignRoleMutationShowSuccessIndicator.value = true;

    setTimeout(() => {
      assignRoleMutationShowSuccessIndicator.value = false;
    }, 2000);
  } else {
    console.error('Assigning new user role failed:', res.data);
    assignRoleMutationError.value =
      'Assigning new user role failed: ' + res.data;
  }
  assignRoleMutationInProgress.value = false;
});

onAssignRoleError((err) => {
  assignRoleMutationError.value = err.message;
  assignRoleMutationInProgress.value = false;
});

function handleAddUserRole(clientId: string, newRole: string) {
  if (!userEmail.value) {
    console.error('User email is not available for adding roles');
    assignRoleMutationError.value =
      'User email is not available for adding roles';
    return;
  }

  if (!newRole) {
    console.error('New role is not defined');
    assignRoleMutationError.value = 'New role is not defined';
    return;
  }

  assignRoleMutationError.value = null;
  assignRoleMutationShowSuccessIndicator.value = false;
  assignRoleMutationInProgress.value = true;

  assignRoleMutate({
    clientId: clientId,
    userEmail: userEmail.value,
    userRole: newRole,
  });
}

// --- REVOKE User Role ---
const revokeRoleMutationError = ref<string | null>(null);
const revokeRoleMutationInProgress = ref(false);
const revokeRoleMutationShowSuccessIndicator = ref(false);

const {
  mutate: revokeRoleMutate,
  onDone: onRevokeRoleDone,
  onError: onRevokeRoleError,
} = useMutation(IamUserRoleRevokeDocument, {
  refetchQueries: [
    {
      query: IamUserRolesDocument,
      variables: { userEmail: userEmail.value },
    },
  ],
  variables: {
    clientId: '',
    userEmail: userEmail.value,
    userRole: '',
  },
});

onRevokeRoleDone((res) => {
  if (res.data && res.data.iamUserRoleRevoke) {
    revokeRoleMutationError.value = null;
    revokeRoleMutationShowSuccessIndicator.value = true;

    setTimeout(() => {
      revokeRoleMutationShowSuccessIndicator.value = false;
    }, 2000);
  } else {
    console.error('Revoking user role failed:', res.data);
    revokeRoleMutationError.value = 'Revoking user role failed: ' + res.data;
  }
  revokeRoleMutationInProgress.value = false;
});

onRevokeRoleError((err) => {
  revokeRoleMutationError.value = err.message;
  revokeRoleMutationInProgress.value = false;
});

function handleRevokeUserRole(clientId: string, userRole: string) {
  if (!userEmail.value) {
    console.error('User email is not available for revoking roles');
    revokeRoleMutationError.value =
      'User email is not available for revoking roles';
    return;
  }

  revokeRoleMutationError.value = null;
  revokeRoleMutationShowSuccessIndicator.value = false;
  revokeRoleMutationInProgress.value = true;

  revokeRoleMutate({
    clientId: clientId,
    userEmail: userEmail.value,
    userRole: userRole,
  });
}

// --- Other functions ---
function handleToggleUserRole(
  event: Event,
  clientId: string,
  userRole: UserRoleType
) {
  if (!userEmail.value) {
    console.error('User email is not available for revoking roles');
    revokeRoleMutationError.value =
      'User email is not available for revoking roles';
    event.preventDefault();
    return;
  }
  if (!clientId) {
    console.error('Client ID is not defined');
    revokeRoleMutationError.value = 'Client ID is not defined';
    event.preventDefault();
    return;
  }
  if (!userRole) {
    console.error('User role is not defined');
    revokeRoleMutationError.value = 'User role is not defined';
    event.preventDefault();
    return;
  }

  if (userRole.assigned) {
    // do not permit revocation if only one role is assigned with the current client
    const client = userRoles.value?.find((client) => client?.id === clientId);
    if (client && client.roles && client.roles.length === 1) {
      revokeRoleMutationError.value =
        'Cannot revoke the only role assigned to this client';
      event.preventDefault();
      return;
    }
    if (userRole.default) {
      console.error('Cannot revoke default role');
      revokeRoleMutationError.value = 'Cannot revoke default role';
      event.preventDefault();
      return;
    }
    // revoke the role
    handleRevokeUserRole(clientId, userRole.name);
  } else {
    handleAddUserRole(clientId, userRole.name);
  }
}

watch(
  userEmail,
  (email) => {
    if (email) {
      requestRefetchUserRoles();
    }
  },
  { immediate: true }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(BButton), {
      onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(openRouterLink)({ name: 'AdminUserList' }, _ctx.$router, $event))),
      variant: "outline-dark",
      class: "mb-3",
      size: "sm"
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(FontAwesomeIcon), {
          icon: "angle-left",
          class: "me-2"
        }),
        _cache[5] || (_cache[5] = _createTextVNode(" Back to Users ", -1))
      ]),
      _: 1
    }),
    _createElementVNode("h1", null, [
      _createVNode(_unref(FontAwesomeIcon), { icon: ['fas', 'user'] }),
      (userData.value?.givenName && userData.value.familyName)
        ? (_openBlock(), _createElementBlock("span", _hoisted_1, _toDisplayString(userData.value.givenName) + " " + _toDisplayString(userData.value.familyName), 1))
        : (_openBlock(), _createElementBlock("span", _hoisted_2, "User details"))
    ]),
    _cache[46] || (_cache[46] = _createElementVNode("hr", null, null, -1)),
    _createVNode(_unref(BAlert), {
      modelValue: _unref(userQueryError),
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(userQueryError) ? (userQueryError).value = $event : null)),
      variant: "danger",
      dismissible: "",
      class: "mt-3"
    }, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref(userQueryError)) + " ", 1),
        _createVNode(_unref(BButton), {
          onClick: _unref(refetchUser),
          variant: "outline-dark",
          class: "btn-sm"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(FontAwesomeIcon), { icon: "sync" }),
            _cache[6] || (_cache[6] = _createTextVNode(" Retry ", -1))
          ]),
          _: 1
        }, 8, ["onClick"])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    (_unref(userQueryInProgress))
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _cache[7] || (_cache[7] = _createTextVNode(" Loading... ", -1)),
          _createVNode(_unref(BSpinner), { small: "" })
        ]))
      : _createCommentVNode("", true),
    (userData.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createElementVNode("p", null, [
            _cache[8] || (_cache[8] = _createElementVNode("strong", null, "ID:", -1)),
            _createTextVNode(" " + _toDisplayString(userData.value.id) + " ", 1),
            _cache[9] || (_cache[9] = _createElementVNode("br", null, null, -1)),
            _cache[10] || (_cache[10] = _createElementVNode("strong", null, "UUID:", -1)),
            _createTextVNode(" " + _toDisplayString(userData.value.uuid) + " ", 1),
            _cache[11] || (_cache[11] = _createElementVNode("br", null, null, -1)),
            _cache[12] || (_cache[12] = _createElementVNode("strong", null, "E-mail: ", -1)),
            _createTextVNode(" " + _toDisplayString(userData.value.email) + " ", 1),
            _cache[13] || (_cache[13] = _createElementVNode("br", null, null, -1)),
            _cache[14] || (_cache[14] = _createElementVNode("strong", null, "Created: ", -1)),
            _createTextVNode(" " + _toDisplayString(_unref(formatDateTime)(userData.value.createdAt)) + " ", 1),
            _cache[15] || (_cache[15] = _createElementVNode("br", null, null, -1))
          ]),
          (
          userData.value.organizationsAssociated &&
          userData.value.organizationsAssociated.length === 0
        )
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _cache[17] || (_cache[17] = _createElementVNode("hr", null, null, -1)),
                _createElementVNode("h2", null, [
                  _createVNode(_unref(FontAwesomeIcon), { icon: ['fas', 'building'] }),
                  _cache[16] || (_cache[16] = _createTextVNode(" No organization associations ", -1))
                ])
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _cache[41] || (_cache[41] = _createElementVNode("hr", null, null, -1)),
                _createElementVNode("h2", null, [
                  _createVNode(_unref(FontAwesomeIcon), { icon: ['fas', 'building'] }),
                  _cache[18] || (_cache[18] = _createTextVNode(" Organization associations ", -1))
                ]),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(userData.value.organizationsAssociated, (assoc, idx) => {
                  return (_openBlock(), _createElementBlock(_Fragment, { key: idx }, [
                    (assoc?.organization)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createElementVNode("h5", null, [
                            _createTextVNode(_toDisplayString(assoc.organization.name) + " ", 1),
                            (!assoc.removedAt)
                              ? (_openBlock(), _createBlock(_unref(BBadge), {
                                  key: 0,
                                  variant: "success",
                                  class: "ms-2"
                                }, {
                                  default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                                    _createTextVNode(" still active ", -1)
                                  ]))]),
                                  _: 1
                                }))
                              : (_openBlock(), _createBlock(_unref(BBadge), {
                                  key: 1,
                                  variant: "secondary",
                                  class: "ms-2"
                                }, {
                                  default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                                    _createTextVNode(" inactive ", -1)
                                  ]))]),
                                  _: 1
                                }))
                          ]),
                          _createElementVNode("p", null, [
                            _cache[23] || (_cache[23] = _createElementVNode("strong", null, "Role:", -1)),
                            _createTextVNode(" " + _toDisplayString(assoc.userRole) + " ", 1),
                            _cache[24] || (_cache[24] = _createElementVNode("br", null, null, -1)),
                            _cache[25] || (_cache[25] = _createElementVNode("strong", null, "From:", -1)),
                            _createTextVNode(" " + _toDisplayString(_unref(formatDateTime)(assoc.createdAt)) + " ", 1),
                            _cache[26] || (_cache[26] = _createElementVNode("br", null, null, -1)),
                            (assoc.removedAt)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_7, [
                                  _cache[21] || (_cache[21] = _createElementVNode("strong", null, "To:", -1)),
                                  _createTextVNode(" " + _toDisplayString(_unref(formatDateTime)(assoc.removedAt)) + " ", 1),
                                  _cache[22] || (_cache[22] = _createElementVNode("br", null, null, -1))
                                ]))
                              : _createCommentVNode("", true),
                            _cache[27] || (_cache[27] = _createElementVNode("strong", null, "ID:", -1)),
                            _createTextVNode(" " + _toDisplayString(assoc.organization.id) + " ", 1),
                            _cache[28] || (_cache[28] = _createElementVNode("br", null, null, -1)),
                            _cache[29] || (_cache[29] = _createElementVNode("strong", null, "SFCD ID:", -1)),
                            _createTextVNode(" " + _toDisplayString(assoc.organization.salesforceId) + " ", 1),
                            _cache[30] || (_cache[30] = _createElementVNode("br", null, null, -1)),
                            _cache[31] || (_cache[31] = _createElementVNode("strong", null, "Street:", -1)),
                            _createTextVNode(" " + _toDisplayString(assoc.organization.addressStreet) + " ", 1),
                            _cache[32] || (_cache[32] = _createElementVNode("br", null, null, -1)),
                            _cache[33] || (_cache[33] = _createElementVNode("strong", null, "City:", -1)),
                            _createTextVNode(" " + _toDisplayString(assoc.organization.addressCity) + " ", 1),
                            _cache[34] || (_cache[34] = _createElementVNode("br", null, null, -1)),
                            _cache[35] || (_cache[35] = _createElementVNode("strong", null, "Postal code:", -1)),
                            _createTextVNode(" " + _toDisplayString(assoc.organization.addressPostalCode) + " ", 1),
                            _cache[36] || (_cache[36] = _createElementVNode("br", null, null, -1)),
                            _cache[37] || (_cache[37] = _createElementVNode("strong", null, "State:", -1)),
                            _createTextVNode(" " + _toDisplayString(assoc.organization.addressState) + " ", 1),
                            _cache[38] || (_cache[38] = _createElementVNode("br", null, null, -1)),
                            _cache[39] || (_cache[39] = _createElementVNode("strong", null, "Country code:", -1)),
                            _createTextVNode(" " + _toDisplayString(assoc.organization.addressCountryCode) + " ", 1),
                            _cache[40] || (_cache[40] = _createElementVNode("br", null, null, -1))
                          ])
                        ], 64))
                      : _createCommentVNode("", true)
                  ], 64))
                }), 128))
              ]))
        ]))
      : _createCommentVNode("", true),
    _createVNode(_unref(BAlert), {
      modelValue: _unref(userRolesQueryError),
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(userRolesQueryError) ? (userRolesQueryError).value = $event : null)),
      variant: "danger",
      dismissible: "",
      class: "mt-3"
    }, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref(userRolesQueryError)?.message) + " ", 1),
        _createVNode(_unref(BButton), {
          onClick: requestRefetchUserRoles,
          variant: "outline-dark",
          class: "btn-sm"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(FontAwesomeIcon), { icon: "sync" }),
            _cache[42] || (_cache[42] = _createTextVNode(" Retry ", -1))
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    (_unref(userRolesQueryInProgress))
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
          _cache[43] || (_cache[43] = _createTextVNode(" Loading user roles... ", -1)),
          _createVNode(_unref(BSpinner), { small: "" })
        ]))
      : _createCommentVNode("", true),
    (userRoles.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _cache[45] || (_cache[45] = _createElementVNode("hr", null, null, -1)),
          _createElementVNode("h2", null, [
            _createVNode(_unref(FontAwesomeIcon), { icon: ['fas', 'user-shield'] }),
            _cache[44] || (_cache[44] = _createTextVNode(" User roles ", -1))
          ]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(userRoles.value, (client, idx) => {
            return (_openBlock(), _createElementBlock(_Fragment, { key: idx }, [
              (!client?.name.startsWith('$') && client?.roles)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createElementVNode("h5", _hoisted_10, _toDisplayString(client?.name), 1),
                    (client?.description)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(client?.description), 1))
                      : _createCommentVNode("", true),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(client?.roles, (role, idx) => {
                      return (_openBlock(), _createElementBlock(_Fragment, { key: idx }, [
                        (client.id && role)
                          ? (_openBlock(), _createBlock(_unref(BFormCheckbox), {
                              key: 0,
                              size: "sm",
                              variant: "outline-danger",
                              class: "ms-2",
                              switch: "",
                              checked: role.assigned,
                              onClick: ($event: any) => (
                handleToggleUserRole($event, client.id, role as UserRoleType)
              )
                            }, {
                              default: _withCtx(() => [
                                _createElementVNode("strong", null, _toDisplayString(role?.name) + "  ", 1),
                                _createElementVNode("small", null, _toDisplayString(role?.description), 1)
                              ]),
                              _: 2
                            }, 1032, ["checked", "onClick"]))
                          : _createCommentVNode("", true)
                      ], 64))
                    }), 128))
                  ], 64))
                : _createCommentVNode("", true)
            ], 64))
          }), 128)),
          _createVNode(_unref(BAlert), {
            modelValue: assignRoleMutationError.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((assignRoleMutationError).value = $event)),
            variant: "danger",
            dismissible: "",
            class: "mt-3"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(assignRoleMutationError.value), 1)
            ]),
            _: 1
          }, 8, ["modelValue"]),
          _createVNode(_unref(BAlert), {
            modelValue: revokeRoleMutationError.value,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((revokeRoleMutationError).value = $event)),
            variant: "danger",
            dismissible: "",
            class: "mt-3"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(revokeRoleMutationError.value), 1)
            ]),
            _: 1
          }, 8, ["modelValue"])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})